import React from 'react';
import { CheckCircle, ArrowRight, Play, Clock } from 'lucide-react';
import { Module } from '../types';

interface ModuleWithProgress extends Module {
  progress?: number;
  completed_at?: Date | null;
  is_unlocked?: boolean;
  is_current?: boolean;
}

interface ModuleNavigationProps {
  modules: ModuleWithProgress[];
  currentModuleId: number;
  onModuleSelect: (moduleId: number) => void;
  courseTitle: string;
}

const ModuleNavigation: React.FC<ModuleNavigationProps> = ({
  modules,
  currentModuleId,
  onModuleSelect,
  courseTitle
}) => {
  const getModuleIcon = (module: ModuleWithProgress) => {
    if (module.completed_at) {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    }
    if (module.is_current) {
      return <ArrowRight className="h-5 w-5 text-blue-500" />;
    }
    return <Play className="h-4 w-4 text-gray-400" />;
  };

  const getModuleStatusText = (module: ModuleWithProgress) => {
    if (module.completed_at) {
      return 'Completed';
    }
    if (module.is_current) {
      return 'Current';
    }
    return 'Available';
  };

  const getModuleStatusColor = (module: ModuleWithProgress) => {
    if (module.completed_at) {
      return 'text-green-600 bg-green-50 border-green-200';
    }
    if (module.is_current) {
      return 'text-blue-600 bg-blue-50 border-blue-200';
    }
    return 'text-gray-700 bg-white border-gray-200 hover:bg-gray-50';
  };

  const getProgressBar = (module: ModuleWithProgress) => {
    const progress = module.progress || 0;
    if (progress === 0) return null;

    return (
      <div className="mt-2">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Progress</span>
          <span>{progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div 
            className="bg-blue-600 h-1.5 rounded-full transition-all duration-300" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white border-r border-gray-200 w-80 h-full overflow-y-auto">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">{courseTitle}</h2>
        <p className="text-sm text-gray-500">
          {modules.filter(m => m.completed_at).length} of {modules.length} modules completed
        </p>
      </div>

      {/* Module List */}
      <div className="p-2">
        {modules.map((module, index) => {
          const isClickable = true;
          
          return (
            <div
              key={module.id}
              className={`
                relative mb-2 p-3 rounded-lg border-2 transition-all duration-200 cursor-pointer
                ${getModuleStatusColor(module)}
                ${module.is_current ? 'ring-2 ring-blue-500 ring-opacity-50' : ''}
              `}
              onClick={() => onModuleSelect(module.id)}
            >
              {/* Module Number Badge */}
              <div className="flex items-start space-x-3">
                <div className={`
                  flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold
                  ${module.completed_at 
                    ? 'bg-green-500 text-white' 
                    : module.is_current 
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700'
                  }
                `}>
                  {index + 1}
                </div>

                <div className="flex-1 min-w-0">
                  {/* Module Title */}
                  <h3 className="text-sm font-medium truncate">
                    {module.title}
                  </h3>

                  {/* Status and Icon */}
                  <div className="flex items-center mt-1 space-x-2">
                    {getModuleIcon(module)}
                    <span className={`
                      text-xs
                      ${module.completed_at 
                        ? 'text-green-600' 
                        : module.is_current 
                          ? 'text-blue-600'
                          : 'text-gray-600'
                      }
                    `}>
                      {getModuleStatusText(module)}
                    </span>
                  </div>

                  {/* Progress Bar */}
                  {getProgressBar(module)}

                  {/* Module Type Badge */}
                  <div className="flex items-center mt-2 space-x-2">
                    {module.is_scorm && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        SCORM
                      </span>
                    )}
                    {module.content_type === 'video/mp4' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        Video
                      </span>
                    )}
                    {module.content_type === 'application/pdf' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                        PDF
                      </span>
                    )}
                  </div>

                  {/* Completion Date */}
                  {module.completed_at && (
                    <div className="flex items-center mt-2 text-xs text-gray-500">
                      <Clock className="h-3 w-3 mr-1" />
                      Completed {new Date(module.completed_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>

              {/* Current Module Indicator */}
              {module.is_current && (
                <div className="absolute -left-1 top-1/2 transform -translate-y-1/2">
                  <div className="w-2 h-8 bg-blue-500 rounded-r"></div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer with Overall Progress */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Course Progress</span>
          <span>
            {Math.round((modules.filter(m => m.completed_at).length / modules.length) * 100)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-green-600 h-2 rounded-full transition-all duration-300" 
            style={{ 
              width: `${(modules.filter(m => m.completed_at).length / modules.length) * 100}%` 
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default ModuleNavigation; 